import { TYPES } from "../ProseMirrorNode.constants";
import getAccessibilityControlsParagraphCount from "./utils/getAccessibilityControlsParagraphCount";
import getAnnotationsEndingRelativeToNodeIdentifier from "./utils/getAnnotationsEndingRelativeToNodeIdentifier";
import getAnnotationsStartingRelativeToNodeIdentifier from "./utils/getAnnotationsStartingRelativeToNodeIdentifier";
import getCurrentNodeOffsets from "./utils/getCurrentNodeOffsets";
import getDoesAnnotationEndInThisNode from "./utils/getDoesAnnotationEndInThisNode";
import getDoesAnnotationStartInThisNode from "./utils/getDoesAnnotationStartInThisNode";
import getIdentifier from "./utils/getIdentifier";
import getUpdatedNodeTypeCount from "./utils/getUpdatedNodeTypeCount";

/**
 * This util moves recursively through the ProseMirror tree and adds data needed for rendering annotations.
 *
 * Three key pieces of information are needed:
 *
 * 1) Node identifiers (see details below)
 * 2) Offset information for text nodes (i.e. how many characters each text node is offset from the node that an annotation would be relative to)
 * 3) Data for annotations that will be rendered within each text node
 */

/**
 * IDENTIFIERS
 *
 * In order to track where an annotation starts and ends, each element in the DOM has an associated identifier.
 * The identifiers are based on its parent element(s), its element type, and the number of elements of that type within its direct parent.
 * For example, the second anchor tag within the third paragraph would have the identifier `/p[3]/a[2]`.
 *
 * The logic for generating these identifiers is based on the annotation plugin currently in use on the Read page.
 * Because of the differences in how the content body is rendered on the View page vs. the Read page, there are some quirks in how these identifiers are generated.
 * See the `getIdentifier` util for more information.
 *
 * Annotations will ultimately be rendered within text nodes, as they wrap text.
 * Text nodes do not always have their own identifier as they do not have always have an associated HTML element,
 * unless they have marks (e.g. a text node with a link mark would be rendered within an anchor tag).
 * To account for this, we add a `relativeIdentifier` property, which is the identifier that an annotation rendered within a text node would have.
 * For text nodes without marks, this would be the parent node's identifier.
 * e.g. For a text node within the third paragraph, the relative identifier would be `/p[3]`.
 * For text nodes with marks, it would be the node's own identifier.
 * e.g. For the first bolded text node within the third paragraph, the relative identifier would be `/p[3]/strong[1]`.
 */

const addAdditionalInfoToProseMirrorNode = ({
  // The data for the current node.
  data,
  // All the annotations within the piece of content.
  annotations,
  // The identifier for the current node's parent.
  parentIdentifier = "",
  // The annotations that start relative to the current node's parent.
  parentAnnotationsWithMatchingStart = [],
  // The annotations that end relative to the current node's parent.
  parentAnnotationsWithMatchingEnd = [],
  // The annotations that could exist in the parent node.
  // We don't know for sure if they exist within the current node until we iterate through to the lowest level of children.
  parentPossibleAnnotations = [],
  highlightedText = {},
  orderedListOfAnnotations = [],
}) => {
  const newData = { ...data };

  // There are no child nodes to iterate through and add new information to, so return the current data.
  if (!newData.content) {
    return {
      ...newData,
      possibleAnnotations: parentPossibleAnnotations,
      highlightedText,
      orderedListOfAnnotations,
    };
  }

  // The count being used by ParagraphCountBadge within specific ProseMirrorNodes
  // as a part of the ParagraphCountAction
  let accessibilityControlsParagraphCount;

  // The count for each type is tracked in order to create the identifiers for the child nodes.
  let nodeTypeCount = {};

  // Track the possible annotations for the current node and the nodes that follow it.
  let possibleAnnotations = [...parentPossibleAnnotations];
  let newHighlightedText = { ...highlightedText };
  let newOrderedListOfAnnotations = [...orderedListOfAnnotations];

  // Track the character offset of the child nodes relative to the current node.
  let startOffsetRelativeToParent;
  let endOffsetRelativeToParent;

  // Map through child nodes and add details relevant to annotations.
  newData.content = data?.content
    ?.filter(
      (childNode) =>
        !(childNode.type === "paragraph" && childNode.content === undefined),
    )
    .map((childNode, index) => {
      nodeTypeCount = getUpdatedNodeTypeCount(childNode, nodeTypeCount);

      const dataIdentifier = childNode.attrs?.["data-identifier"];

      const identifier =
        dataIdentifier ||
        getIdentifier(childNode, nodeTypeCount, parentIdentifier);

      accessibilityControlsParagraphCount =
        getAccessibilityControlsParagraphCount(
          childNode,
          accessibilityControlsParagraphCount,
          parentIdentifier,
          data.content,
          index,
        );

      // The annotations with a `start` property equal to the current node's identifier.
      const annotationsWithMatchingStart =
        getAnnotationsStartingRelativeToNodeIdentifier(annotations, identifier);
      // The annotations with a `end` property equal to the current node's identifier.
      const annotationsWithMatchingEnd =
        getAnnotationsEndingRelativeToNodeIdentifier(annotations, identifier);
      // Add any annotations starting in the child node to the possible current node annotations array.
      if (annotationsWithMatchingStart?.length) {
        annotationsWithMatchingStart.forEach((annotation) => {
          possibleAnnotations.push({ ...annotation });
        });
      }

      const currentNodeOffsetsRelativeToParent = getCurrentNodeOffsets(
        childNode,
        endOffsetRelativeToParent,
      );
      startOffsetRelativeToParent =
        currentNodeOffsetsRelativeToParent.startOffset;
      endOffsetRelativeToParent = currentNodeOffsetsRelativeToParent.endOffset;

      // If the child node has its own identifier, the offsets should be relative to it rather than the parent.
      const startOffset = identifier ? 0 : startOffsetRelativeToParent;
      const endOffset =
        identifier && childNode.text
          ? childNode.text.length
          : endOffsetRelativeToParent;

      const annotationsRenderedWithinCurrentNode = [];

      // Add details about whether each annotation has started and ended in the current node.
      possibleAnnotations.forEach((annotation, index) => {
        const nodeDetails = {
          identifier,
          startOffset,
          endOffset,
          parentIdentifier,
        };

        const doesAnnotationStartInNode = getDoesAnnotationStartInThisNode(
          annotationsWithMatchingStart,
          parentAnnotationsWithMatchingStart,
          nodeDetails,
          annotation,
        );

        const doesAnnotationEndInNode = getDoesAnnotationEndInThisNode(
          annotationsWithMatchingEnd,
          parentAnnotationsWithMatchingEnd,
          nodeDetails,
          annotation,
        );

        // Both text and hard breaks are base-level blocks, with no children.
        // Because of this, annotations might start or end within these nodes.
        if (
          childNode.type === TYPES.text ||
          childNode.type === TYPES.hardBreak
        ) {
          const hasAnnotationEnded =
            doesAnnotationEndInNode || possibleAnnotations[index].hasEnded;
          const hasAnnotationStarted =
            doesAnnotationStartInNode || possibleAnnotations[index].hasStarted;

          // Possible scenarios:
          // 1. Annotation has started and has not ended: this annotation will be rendered within the current text node.
          // 2. Annotation has started and has ended BUT the end is in this node: this annotation will be rendered within the current text node.
          // 3. Annotation has started and has ended AND the end is not in this node: this annotation will be not rendered within the current node.
          const doesAnnotationRenderInNode =
            hasAnnotationStarted &&
            (!hasAnnotationEnded || doesAnnotationEndInNode);

          if (doesAnnotationRenderInNode) {
            // Build array of all text nodes containing highlighted text.
            if (!newHighlightedText[annotation.id]) {
              // Update ordered list of annotations to add the newly rendered annotation.
              newOrderedListOfAnnotations.push(annotation);
              newHighlightedText[annotation.id] = [
                {
                  ...childNode,
                  identifier,
                  parentIdentifier,
                  startOffset,
                  endOffset,
                },
              ];
            } else {
              newHighlightedText[annotation.id].push({
                ...childNode,
                identifier,
                parentIdentifier,
                startOffset,
                endOffset,
              });
            }

            annotationsRenderedWithinCurrentNode.push({
              ...annotation,
              isEndInNode: doesAnnotationEndInNode,
              isStartInNode: doesAnnotationStartInNode,
              hasEnded: hasAnnotationEnded,
              hasStarted: hasAnnotationStarted,
            });
          }

          possibleAnnotations[index] = {
            ...annotation,
            hasEnded: hasAnnotationEnded,
            hasStarted: hasAnnotationStarted,
          };
        }
      });

      // Remove annotations that have ended in the current node from possible annotations for the next node.
      possibleAnnotations = possibleAnnotations.filter(
        (annotation) => !annotation.hasEnded,
      );

      // Call the function recursively to traverse this child node's children.
      const childNodeData = addAdditionalInfoToProseMirrorNode({
        data: childNode,
        parentIdentifier: identifier || parentIdentifier,
        parentAnnotationsWithMatchingStart: [
          ...annotationsWithMatchingStart,
          ...parentAnnotationsWithMatchingStart,
        ],
        parentPossibleAnnotations: possibleAnnotations,
        parentAnnotationsWithMatchingEnd: [
          ...annotationsWithMatchingEnd,
          ...parentAnnotationsWithMatchingEnd,
        ],
        annotations,
        highlightedText: newHighlightedText,
        orderedListOfAnnotations: newOrderedListOfAnnotations,
      });

      // Get possible annotations found in child node's children.
      const {
        possibleAnnotations: childPossibleAnnotations = [],
        highlightedText: childHighlightedText,
        orderedListOfAnnotations: childOrderedListOfAnnotations,
        ...childData
      } = childNodeData;

      // Update the possible annotations to include those from child node's children.
      possibleAnnotations = childPossibleAnnotations;
      newHighlightedText = childHighlightedText;
      newOrderedListOfAnnotations = childOrderedListOfAnnotations;

      return {
        ...childData,
        accessibilityControlsParagraphCount,
        identifier,
        relativeIdentifier: identifier || parentIdentifier,
        // Annotations are only rendered within text nodes.
        ...(childNode.type === TYPES.text
          ? {
              currentAnnotations: annotationsRenderedWithinCurrentNode,
              endOffset,
              identifier,
              isPowerWordChild: [TYPES.powerWord, TYPES.keyTerm].includes(
                data?.type,
              ),
              startOffset,
              startOffsetRelativeToParent,
            }
          : {}),
      };
    });
  return {
    ...newData,
    possibleAnnotations,
    highlightedText: newHighlightedText,
    orderedListOfAnnotations: newOrderedListOfAnnotations,
  };
};
export default addAdditionalInfoToProseMirrorNode;
